import { assign, fromPromise, setup } from 'xstate';
import { TimeZone } from 'domains/timezone';
import { getConsultationAvailableTimes } from 'domains/calendars/queries';
import { AvailableTimes } from 'domains/calendars/types';
import { DEFAULT_APPOINTMENT_TYPE } from 'domains/signup/data/queryParams';
import { SignupMachineInput } from 'domains/signup/signupFlow/signupMachine';
import { MachineStateType } from 'utils/xstate/types';

export type CompletedScheduleMachineContext = {
  availableConsultDates: AvailableTimes;
  leadID: string;
  consultationDate: Date;
  consultationTimezone: TimeZone;
};

export type ScheduleResult = CompletedScheduleMachineContext;

export type ScheduleMachineInput = {
  machineConfig: Pick<SignupMachineInput, 'calendarID'> & Required<Pick<SignupMachineInput, 'appointmentType'>>;
};

/** @xstate-layout N4IgpgJg5mDOIC5SwMYAtIFcA2YCyAhugJYB2YAdGKQC5gBOAwgPamw40E3GsAiXYAMTsARgFtiNFmw5cepfnQDaABgC6iUAAdmsSfM0gAHogC0AFgDsligCYVAVgAcANhUBGR+YCcv8wBoQAE9EAGZbbwpvd1tbF1Cnb3sI2wBfVMDUDAgcfCI0MkoUVgAzYnoxaXZsTm4+AUERIgBrVQ0kEB09OtJDEwRTS1t3CiHLFydLFUt3B2iXQJCEc1tQiicHK1iPFRcXWwcXdMz0LFxCEnIKYtIyiqrZHsUhG7uxSAeauXrldUMu-SsPpmcyJCihbyhULmUIqGGxHyLRDxWzrSzmByWbwTFTDdzHEBZM55S6UbDMAgQMhQACCADcCMRsAQRLhnrBBBBWJQyHTmM1KEScud8oUKOTKdT6YzmaywOyELzmChvqQ2m1-rpAb0Ov0LN4VOtIdCXJZnJjLE4kQNhhR3NDvHMnOY4VZ9gShbkLgUrhKqaRaQymSy2QIOQx6Mx6BQtMyaCUo2IKJ6RaTxRT-YGZSH5WHFaQ+Sqeuq-h0AT1gQN7S47d4nCpvOYMZZQu461bgmYYjZrI4nG3bE5bKDQekMiBSMwIHBDCmST6wJrugZdWZxpExhMpjM5u4Fp2bS67FM5qFrBMDWePadhfOxdQ6ExWNVavJnkvtZWLC5Ig5PA5wkOetYjPa09zWfZQj-UEnFcFtQiOcc529MVXnKSpn0eN8BA-CtVwGKwnDsat4k2Fw9xWa0-1RFxQVozEAN8DZr2yL1RV9DMpSDWVQzoeAyy1PDQD1Bw4QoF13HcVx4kmexsWtUxZlRXtnANLc3RY4kUI4yUA3ZAAxGVIFwldhK7XFxPCDFNl8BsfyokYXUtFQG2hcwXE2cxNNvbTKDKUhiFgbITKBfCLDPcFVlxdEnHCWLoWtS0HDtfYQKHdwhlcMdUiAA */
export const scheduleMachine = setup({
  types: {
    context: {} as Partial<CompletedScheduleMachineContext> & ScheduleMachineInput,
    events: {} as
      | { type: 'back' }
      | { type: 'submitConsultationDate'; consultationDate: Date; consultationTimezone: TimeZone }
      | { type: 'confirmedConsultationDate' },

    input: {} as ScheduleMachineInput,
    output: {} as ScheduleResult,
  },

  actions: {
    saveConsultationDate: assign((_, params: { consultationDate: Date; consultationTimezone: TimeZone }) => ({
      consultationDate: params.consultationDate,
      consultationTimezone: params.consultationTimezone,
    })),
    saveAvailableConsultDates: assign((_, params: { data: AvailableTimes }) => ({
      availableConsultDates: params.data,
    })),
  },

  actors: {
    getAvailableConsultDates: fromPromise(
      async ({ input }: { input: { calendarID?: string; appointmentType: string } }) => {
        return getConsultationAvailableTimes({
          appointmentType: input.appointmentType,
          calendarID: input.calendarID,
        });
      },
    ),
  },
  guards: {},
}).createMachine({
  id: 'scheduleMachine',
  initial: 'loadingAvailableDates',

  context: ({
    input: {
      machineConfig: { appointmentType, calendarID },
    },
  }) => ({
    machineConfig: {
      appointmentType: appointmentType ?? DEFAULT_APPOINTMENT_TYPE,
      calendarID: calendarID,
    },
  }),

  states: {
    enterConsultationDate: {
      on: {
        submitConsultationDate: {
          target: 'confirmConsultationDate',

          actions: {
            type: 'saveConsultationDate',
            params: ({ event: { consultationDate, consultationTimezone } }) => ({
              consultationDate,
              consultationTimezone,
            }),
          },
        },
      },
    },

    confirmConsultationDate: {
      on: {
        back: 'enterConsultationDate',
        confirmedConsultationDate: 'finished',
      },
    },

    loadingAvailableDates: {
      invoke: {
        src: 'getAvailableConsultDates',
        input: ({
          context: {
            machineConfig: { appointmentType, calendarID },
          },
        }) => ({ appointmentType: appointmentType, calendarID }),
        onDone: {
          target: 'enterConsultationDate',
          actions: {
            type: 'saveAvailableConsultDates',
            params: ({ event: { output } }) => ({
              data: output,
            }),
          },
        },
        onError: 'loadingDatesFailed',
      },
    },

    loadingDatesFailed: {},

    finished: {
      type: 'final',
    },
  },
  output: ({ context }) => context as ScheduleResult,
});

export type ScheduleState = MachineStateType<typeof scheduleMachine>;
